import React, { Fragment, useCallback } from 'react';

import { CategoryID } from '../../../../categories/categoriesTypes';

import { FetchProductCategoriesQueryResponse } from '../../../../products/queries/fetchProductCategories.query';

import { useThreeDStockBreadcrumbs } from './hooks/useThreeDStockBreadcrumbs';

import { ThreeDStockBreadcrumbsChevron } from './components/ThreeDStockBreadcrumbsChevron';
import { ThreeDStockBreadcrumbsItem } from './components/ThreeDStockBreadcrumbsItem';

import { PureButtonHelper } from '../../../../../helpers/buttons/PureButtonHelper';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';
import { Translate } from '../../../../../helpers/Translate';

import { words } from '../../../../../locales/keys';

interface ThreeDStockBreadcrumbsProps {
  i18nBaseTitle?: string;
  onSelect: (categoryId?: CategoryID) => void;
  currentCategoryId?: CategoryID;
  categories?: FetchProductCategoriesQueryResponse[];
  categoriesFetched?: boolean;
  isLibrary?: boolean;
}

function ThreeDStockBreadcrumbs({
  i18nBaseTitle,
  currentCategoryId,
  categories,
  categoriesFetched,
  onSelect,
  isLibrary
}: ThreeDStockBreadcrumbsProps) {
  const { breadcrumbs } = useThreeDStockBreadcrumbs({
    currentCategoryId,
    categories
  });

  const handleClickBaseItem = useCallback<() => void>(
    () => onSelect(),
    [onSelect]
  );

  return (
    <div id="3d-stock-breadcrumbs" className="flex gap-1 text-sm">
      <span className="text-blue-600 dark:text-blue-400 focus:ring-0 focus:ring-offset-0 cursor-default">
        <Translate id={isLibrary ? words.myLibrary : words.threeDStock} />
      </span>

      <ThreeDStockBreadcrumbsChevron />

      {currentCategoryId ? (
        <PureButtonHelper
          dataGa="category-breadcrumb-home"
          className="text-blue-600 dark:text-blue-400 hover:underline focus:ring-0 focus:ring-offset-0"
          i18nText={i18nBaseTitle}
          onClick={handleClickBaseItem}
        />
      ) : (
        <span className="font-semibold cursor-default">
          <Translate id={i18nBaseTitle} />
        </span>
      )}

      {!categoriesFetched && currentCategoryId && (
        <Fragment>
          <ThreeDStockBreadcrumbsChevron />
          <LoadingSkeleton
            loaded={categoriesFetched}
            className="w-6"
            count={1}
          />
        </Fragment>
      )}

      {breadcrumbs.map((item, index) => (
        <Fragment key={item.id}>
          <ThreeDStockBreadcrumbsChevron />
          {index === breadcrumbs.length - 1 ? (
            <span className="font-semibold cursor-default">
              {item.localizedName}
            </span>
          ) : (
            <ThreeDStockBreadcrumbsItem
              dataGa={`category-breadcrumb-${item.id}`}
              item={item}
              onSelect={onSelect}
            />
          )}
        </Fragment>
      ))}
    </div>
  );
}

export default ThreeDStockBreadcrumbs;
